import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import BusinessForm from '../components/BusinessForm';
import PointOfSaleForm from '../components/PointOfSaleForm';
import SalesCharts from '../components/SalesCharts';
import AdminDashboardCharts from '../components/AdminDashboardCharts';
import AdminSubscriptionsTable from '../components/AdminSubscriptionsTable';
import SubscriptionPlans from '../components/SubscriptionPlans';
import PaymentInfoCards from '../components/PaymentInfoCards';
import Modal from '../components/Modal';
import AdminTopProducts from '../components/AdminTopProducts'; // Componente de productos TOP
import AdminRecentSales from '../components/AdminRecentSales'; // Componente de ventas recientes
import ChatWidget from '../components/ChatWidget'; // Asistente IA Ventasz
import '../styles/Dashboard.css';
import '../styles/BusinessForm.css';
import '../styles/PointOfSaleForm.css';
import '../styles/AdminSubscriptionsTable.css';
import '../styles/SubscriptionPlans.css';
import '../styles/PaymentInfoCards.css';
import '../styles/Modal.css';
import '../styles/DashboardButtons.css';

const Dashboard = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [adminRoleStats, setAdminRoleStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [showBusinessForm, setShowBusinessForm] = useState(false);
  const [business, setBusiness] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(false);
  
  // Estado para el modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState('error');
  
  // Points of sale state
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [pointsOfSaleLoading, setPointsOfSaleLoading] = useState(false);
  const [showPointOfSaleForm, setShowPointOfSaleForm] = useState(false);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  
  // Currency rate state
  const [currencyRate, setCurrencyRate] = useState(null);

  // Estados para estadísticas de ventas
  const [salesStats, setSalesStats] = useState(null);
  const [salesStatsLoading, setSalesStatsLoading] = useState(false);
  const [selectedPOS, setSelectedPOS] = useState('all');
  
  // Estado para ventas recientes
  const [recentSales, setRecentSales] = useState([]);
  const [recentSalesLoading, setRecentSalesLoading] = useState(false);
  
  // Estado para productos más vendidos
  const [topProducts, setTopProducts] = useState([]);
  const [topProductsLoading, setTopProductsLoading] = useState(false);
  

  
  // Estado para el plan de suscripción seleccionado
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Estados para controlar la habilitación/deshabilitación de los menús
  // Estado para controlar si hay puntos de venta registrados y al menos uno está abierto
  // Inicialmente true para evitar que el menú se deshabilite antes de cargar los datos reales
  const [hasActivePointsOfSale, setHasActivePointsOfSale] = useState(true);
  // Estado para controlar si la suscripción está suspendida
  const [subscriptionSuspended, setSubscriptionSuspended] = useState(false);
  // Estado para controlar si el usuario tiene una suscripción registrada
  const [hasSubscription, setHasSubscription] = useState(false);
  // Estado para controlar si hay puntos de venta pero todos están cerrados
  const [allPointsOfSaleClosed, setAllPointsOfSaleClosed] = useState(false);
  // Estado para controlar si el usuario ya tiene vendedores asignados
  const [hasAssignedSellers, setHasAssignedSellers] = useState(false);
  // Estado que combina todas las condiciones para habilitar/deshabilitar el menú
  // El menú debe estar habilitado si: hay puntos de venta activos Y la suscripción no está suspendida Y existe un registro de suscripción
  const [menuEnabled, setMenuEnabled] = useState(false); // Inicialmente deshabilitado hasta que se verifiquen todas las condiciones
  // Estado para controlar si los datos del menú se están cargando
  const [menuLoading, setMenuLoading] = useState(true);

  // Fetch user stats
  const fetchUserStats = async () => {
    // This is a placeholder for future API calls to get user-specific data
    // For now, we'll just set some dummy data
    setUserStats({
      totalSales: 0,
      pendingOrders: 0,
      recentActivity: []
    });
  };

  // Fetch admin stats for master users
  const fetchAdminStats = async () => {
    if (user && user.role === 'master') {
      try {
        setStatsLoading(true);
        setStatsError(null);
        const token = localStorage.getItem('token');
        
        const response = await axios.get('/api/users/stats', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setAdminStats(response.data);
      } catch (error) {
        console.error('Error fetching admin stats:', error);
        setStatsError('Failed to load user statistics. Please try again later.');
      } finally {
        setStatsLoading(false);
      }
    }
  };
  
  // Fetch admin role stats for admin users
  const fetchAdminRoleStats = async () => {
    if (user && user.role === 'admin') {
      try {
        setStatsLoading(true);
        setStatsError(null);
        const token = localStorage.getItem('token');
        
        const response = await axios.get('/api/users/admin-stats', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setAdminRoleStats(response.data);
      } catch (error) {
        console.error('Error fetching admin role stats:', error);
        setStatsError('Failed to load admin statistics. Please try again later.');
      } finally {
        setStatsLoading(false);
      }
    }
  };

  // Fetch points of sale for the business
  const fetchPointsOfSale = async () => {
    if (user && user.role === 'admin') {
      try {
        console.log('=== FETCH POS DEBUG ===');
        console.log('window.innerWidth:', window.innerWidth);
        console.log('100vw calculated:', window.innerWidth + 'px');
        console.log('======================');
        
        setPointsOfSaleLoading(true);
        setMenuLoading(true); // Indicar que estamos cargando los datos del menú
        const token = localStorage.getItem('token');
        
        const response = await axios.get('/api/points-of-sale', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Manejar la nueva estructura de respuesta
        if (response.data && response.data.pointsOfSale) {
          // Guardar los puntos de venta en el estado
          setPointsOfSale(response.data.pointsOfSale);
          
          // Verificar si hay puntos de venta registrados
          const hasPointsOfSale = response.data.metadata && response.data.metadata.total > 0;
          
          // Verificar si hay puntos de venta abiertos usando los metadatos
          const hasActive = response.data.metadata && response.data.metadata.hasActivePointsOfSale === true;
          
          console.log('DEBUG FRONTEND - Puntos de venta totales:', response.data.metadata.total);
          console.log('DEBUG FRONTEND - Puntos de venta activos:', response.data.metadata.active);
          console.log('DEBUG FRONTEND - ¿Hay puntos de venta activos según metadatos?', hasActive);
          
          // Verificar directamente en los datos si hay puntos de venta abiertos
          let hasActiveFromData = false;
          if (response.data.pointsOfSale && response.data.pointsOfSale.length > 0) {
            hasActiveFromData = response.data.pointsOfSale.some(pos => pos.pos_closed === false);
            console.log('DEBUG FRONTEND - ¿Hay puntos de venta activos según datos directos?', hasActiveFromData);
          }
          
          // Usar la verificación directa de los datos como fuente de verdad
          const finalHasActive = hasActiveFromData;
          console.log('DEBUG FRONTEND - Decisión final: ¿Hay puntos de venta activos?', finalHasActive);
          
          // Establecer si hay puntos de venta activos
          setHasActivePointsOfSale(finalHasActive);
          
          // Establecer si hay puntos de venta pero todos están cerrados
          // Verificamos directamente en los datos recibidos
          let allClosed = false;
          
          if (hasPointsOfSale && response.data.pointsOfSale && response.data.pointsOfSale.length > 0) {
            // Mostrar todos los puntos de venta para depuración
            console.log('DEBUG FRONTEND - Todos los puntos de venta:', response.data.pointsOfSale);
            
            // Verificar si hay al menos un punto de venta abierto
            // Un punto de venta está abierto si pos_closed es false
            const openPointsOfSale = response.data.pointsOfSale.filter(pos => pos.pos_closed === false);
            console.log('DEBUG FRONTEND - Puntos de venta abiertos encontrados:', openPointsOfSale.length);
            console.log('DEBUG FRONTEND - Detalles de puntos de venta abiertos:', openPointsOfSale);
            
            // Si hay puntos de venta pero ninguno está abierto, entonces todos están cerrados
            allClosed = openPointsOfSale.length === 0 && response.data.pointsOfSale.length > 0;
          }
          
          console.log('DEBUG FRONTEND - ¿Todos los puntos de venta están cerrados?', allClosed);
          setAllPointsOfSaleClosed(allClosed);
          
          // Actualizar el estado del menú basado en las condiciones
          // El menú debe estar habilitado solo si hay puntos de venta activos Y la suscripción no está suspendida
          console.log('DEBUG FRONTEND - Valores para calcular shouldEnableMenu:', {
            hasActive,
            allClosed,
            subscriptionSuspended,
            'hasActive && !subscriptionSuspended': hasActive && !subscriptionSuspended
          });
          // Ya no actualizamos directamente el estado del menú aquí
          // El useEffect dedicado se encargará de actualizar el estado del menú
          // basado en los cambios en hasActivePointsOfSale y subscriptionSuspended
          console.log('DEBUG FRONTEND - fetchPointsOfSale: Actualizando hasActivePointsOfSale a', hasActive);
          setMenuLoading(false); // Indicar que hemos terminado de cargar los datos del menú
        } else {
          // Formato de respuesta anterior o error
          console.warn('Formato de respuesta inesperado:', response.data);
          // Si hay datos, asumimos que hay al menos un punto de venta
          if (response.data && Array.isArray(response.data) && response.data.length > 0) {
            setPointsOfSale(response.data);
            // Verificamos si hay al menos un punto de venta abierto
            // Un punto de venta está abierto si pos_closed es false
            const hasOpenPOS = response.data.some(pos => pos.pos_closed === false);
            console.log('DEBUG FRONTEND - Verificando puntos de venta abiertos en formato antiguo:', hasOpenPOS);
            console.log('DEBUG FRONTEND - Detalles de puntos de venta en formato antiguo:', response.data);
            setHasActivePointsOfSale(hasOpenPOS);
            setAllPointsOfSaleClosed(!hasOpenPOS && response.data.length > 0);
          } else {
            setPointsOfSale([]);
            setHasActivePointsOfSale(false);
            setAllPointsOfSaleClosed(false);
          }
          setMenuLoading(false); // Finalizar el estado de carga del menú
        }
      } catch (error) {
        console.error('Error fetching points of sale:', error);
        // En caso de error, mantenemos el estado actual de hasActivePointsOfSale
        // para evitar deshabilitar el menú prematuramente
        console.log('DEBUG FRONTEND - Error al obtener puntos de venta, manteniendo estado actual');
        setPointsOfSaleLoading(false);
        setMenuLoading(false); // Finalizar el estado de carga del menú
        // Si no hay puntos de venta cargados, establecemos un array vacío
        if (!pointsOfSale || pointsOfSale.length === 0) {
          setPointsOfSale([]);
          setHasActivePointsOfSale(false);
          setAllPointsOfSaleClosed(false);
        }
      } finally {
        setPointsOfSaleLoading(false);
      }
    }
  };

  // Verificar si el usuario tiene vendedores asignados
  const checkAssignedSellers = async () => {
    if (user && user.role === 'admin') {
      try {
        const token = localStorage.getItem('token');
        
        const response = await axios.get('/api/sellers/has-assigned-sellers', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setHasAssignedSellers(response.data.hasSellers);
        console.log('DEBUG FRONTEND - ¿Tiene vendedores asignados?', response.data.hasSellers);
      } catch (error) {
        console.error('Error al verificar vendedores asignados:', error);
        setHasAssignedSellers(false);
      }
    }
  };

  // Fetch business data for admin users
  const fetchBusinessData = async () => {
    if (user && user.role === 'admin') {
      try {
        setBusinessLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await axios.get('/api/businesses/me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setBusiness(response.data);
        
        // If business exists, fetch points of sale and subscription
        if (response.data) {
          // Primero obtenemos los puntos de venta
          await fetchPointsOfSale();
          // Luego la suscripción
          await fetchSubscription();
          // Verificar si tiene vendedores asignados
          await checkAssignedSellers();
          // Y finalmente las estadísticas que dependen de los puntos de venta
          await fetchSalesStats();
          await fetchRecentSales();

          await fetchTopProducts();
        } else {
          // Si no hay negocio, no hay puntos de venta activos
          setHasActivePointsOfSale(false);
          setAllPointsOfSaleClosed(false);
        }
      } catch (error) {
        // If 404, it means the user doesn't have a business yet
        if (error.response && error.response.status === 404) {
          setBusiness(null);
          // Si no hay negocio, no hay puntos de venta activos
          setHasActivePointsOfSale(false);
          setAllPointsOfSaleClosed(false);
        } else {
          console.error('Error fetching business data:', error);
          // En caso de error, no hay puntos de venta activos
          setHasActivePointsOfSale(false);
          setAllPointsOfSaleClosed(false);
        }
      } finally {
        setBusinessLoading(false);
      }
    }
  };
  
  // Fetch subscription information for the user
  const fetchSubscription = async () => {
    console.log('DEBUG - Usuario actual:', user?.id, user?.role);
    if (user && user.role === 'admin') {
      try {
        setSubscriptionLoading(true);
        setMenuLoading(true); // Indicar que estamos cargando los datos del menú
        const token = localStorage.getItem('token');
        
        const response = await axios.get('/api/subscriptions/me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Verificar si existe un registro de suscripción para el usuario
        const subscriptionExists = response.data && response.data.id;
        console.log('DEBUG FRONTEND - ¿Existe registro de suscripción?', subscriptionExists);
        
        // Actualizar el estado de suscripción
        setHasSubscription(subscriptionExists);
        
        // Si no existe registro de suscripción, desactivar el menú
        if (!subscriptionExists) {
          console.log('DEBUG FRONTEND - No existe registro de suscripción para el usuario');
          setMenuEnabled(false);
          setSubscription(null);
          setMenuLoading(false);
          setSubscriptionLoading(false);
          return;
        }
        
        setSubscription(response.data);
        
        // Verificar si la suscripción está suspendida
        const isSuspended = response.data && 
                           response.data.status && 
                           response.data.status.toLowerCase() === 'suspendido';
        setSubscriptionSuspended(isSuspended);
        console.log('DEBUG FRONTEND - Estado de suscripción:', response.data?.status);
        console.log('DEBUG FRONTEND - ¿Suscripción suspendida?', isSuspended);
        
        // Actualizar el estado del menú basado en las condiciones
        // El menú debe estar habilitado solo si hay puntos de venta activos Y la suscripción no está suspendida
        console.log('DEBUG FRONTEND - Valores para calcular shouldEnableMenu en fetchSubscription:', {
          hasActivePointsOfSale,
          isSuspended,
          'hasActivePointsOfSale && !isSuspended': hasActivePointsOfSale && !isSuspended
        });
        // Ya no actualizamos directamente el estado del menú aquí
        // El useEffect dedicado se encargará de actualizar el estado del menú
        // basado en los cambios en hasActivePointsOfSale y subscriptionSuspended
        console.log('DEBUG FRONTEND - fetchSubscription: Actualizando subscriptionSuspended a', isSuspended);
        setMenuLoading(false); // Finalizar el estado de carga del menú
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        // Si hay un error al obtener la suscripción, asumimos que no existe
        setSubscription(null);
        setMenuEnabled(false); // Desactivar el menú si no se puede obtener la suscripción
        setMenuLoading(false); // Finalizar el estado de carga del menú en caso de error
      } finally {
        setSubscriptionLoading(false);
      }
    }
  };

  // Fetch sales statistics
  const fetchSalesStats = async (posId = null) => {
    if (user && user.role === 'admin') {
      try {
        setSalesStatsLoading(true);
        const token = localStorage.getItem('token');
        console.log('Token para estadísticas:', token ? 'Existe' : 'No existe');
        
        let url = '/api/stats/sales';
        if (posId && posId !== 'all') {
          url += `?pointOfSaleId=${posId}`;
        }
        console.log('Solicitando estadísticas de ventas a:', url);
        
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('Respuesta de estadísticas:', response.data);
        setSalesStats(response.data);
      } catch (error) {
        console.error('Error fetching sales stats:', error);
        console.error('Detalles del error:', error.response ? error.response.data : 'No hay detalles');
      } finally {
        setSalesStatsLoading(false);
      }
    } else {
      console.log('No se solicitan estadísticas porque el usuario no es admin o no está definido:', user?.role);
    }
  };

  // Fetch recent sales
  const fetchRecentSales = async () => {
    try {
      setRecentSalesLoading(true);
      const token = localStorage.getItem('token');
      
      // Construir URL con el filtro de punto de venta si es necesario
      let url = '/api/admin-stats/recent-sales';
      if (selectedPOS && selectedPOS !== 'all') {
        url += `?pointOfSaleId=${selectedPOS}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.data) {
        setRecentSales(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching recent sales:', error);
    } finally {
      setRecentSalesLoading(false);
    }
  };
  
  // Función para recargar los datos después de cancelar una venta
  const handleReloadAfterCancel = () => {
    fetchRecentSales();
    fetchSalesStats(selectedPOS);

    fetchTopProducts();
  };
  

  
  // Fetch top products
  const fetchTopProducts = async () => {
    if (!user) return;
    
    setTopProductsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/stats/top-products', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setTopProducts(response.data.data || []);
    } catch (error) {
      console.error('Error al obtener productos más vendidos:', error);
    } finally {
      setTopProductsLoading(false);
    }
  };

  // Fetch currency rate for business country
  const fetchCurrencyRate = async () => {
    if (user && user.role === 'admin') {
      try {
        const token = localStorage.getItem('token');
        console.log('Making request to /api/businesses/currency-rate with token:', token ? 'Present' : 'Missing');
        const response = await axios.get('/api/businesses/currency-rate', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('=== FRONTEND CURRENCY DEBUG ===');
        console.log('API URL called:', '/api/businesses/currency-rate');
        console.log('Currency rate response:', response.data);
        console.log('Exchange rate value:', response.data.exchange_rate);
        console.log('Exchange rate type:', typeof response.data.exchange_rate);
        console.log('Response status:', response.status);
        setCurrencyRate(response.data);
      } catch (error) {
        console.error('Error fetching currency rate:', error);
        console.error('Error details:', error.response?.data);
        console.error('Error status:', error.response?.status);
        // Default to USD if error
        setCurrencyRate({
          currency_code: 'USD',
          currency_symbol: '$',
          exchange_rate: 1.0000
        });
      }
    }
  };

  // Handle point of sale change for stats filtering
  const handlePOSChange = (e) => {
    const posId = e.target.value;
    console.log('Cambiando punto de venta a:', posId);
    
    // Actualizar estadísticas de ventas
    fetchSalesStats(posId);
    
    // Forzar recarga de componentes dependientes del punto de venta seleccionado
    console.log('Actualizando componentes con nuevo punto de venta:', posId);
    
    // Actualizar el estado para que todos los componentes que dependen de él se actualicen
    // Esto debe ser lo último para garantizar que los componentes detecten el cambio
    setSelectedPOS(posId);
  };

  // Fetch user stats or other dashboard data
  useEffect(() => {
    if (user) {
      console.log('Dashboard cargado con usuario:', user);
      console.log('Token almacenado:', localStorage.getItem('token'));
      
      // Primero obtenemos los datos del negocio, que a su vez obtendrá los puntos de venta
      fetchBusinessData();
      
      // Obtenemos las estadísticas del admin
      fetchAdminStats();
      fetchAdminRoleStats();
      
      // Obtener tipo de cambio para conversión de moneda
      fetchCurrencyRate();
      

    }
  }, [user]);

  // Este useEffect se encarga específicamente de mantener actualizado el estado del menú
  // basado en los estados de puntos de venta y suscripción
  useEffect(() => {
    console.log('DEBUG FRONTEND - useEffect para actualizar estado del menú - TIMESTAMP:', new Date().toISOString());
    console.log('DEBUG FRONTEND - useEffect para actualizar estado del menú:', {
      hasActivePointsOfSale,
      allPointsOfSaleClosed,
      subscriptionSuspended,
      menuLoading,
      pointsOfSale: pointsOfSale.length,
      hasSubscription,
      'subscription': subscription ? 'Existe' : 'No existe',
      'hasActivePointsOfSale && !allPointsOfSaleClosed && !subscriptionSuspended && hasSubscription': 
        hasActivePointsOfSale && !allPointsOfSaleClosed && !subscriptionSuspended && hasSubscription
    });
    
    // DEBUG: Capturar dimensiones de pantalla en cada re-render
    console.log('=== DIMENSION DEBUG ===');
    console.log('TIMESTAMP:', new Date().toISOString());
    console.log('window.innerWidth:', window.innerWidth);
    console.log('window.innerHeight:', window.innerHeight);
    console.log('document.documentElement.clientWidth:', document.documentElement.clientWidth);
    console.log('document.documentElement.clientHeight:', document.documentElement.clientHeight);
    console.log('screen.width:', window.screen.width);
    console.log('screen.height:', window.screen.height);
    console.log('100vw calculated:', window.innerWidth + 'px');
    console.log('100vh calculated:', window.innerHeight + 'px');
    
    // DEBUG: Verificar elementos dashboard-container
    const dashboardContainer = document.querySelector('.dashboard-container');
    if (dashboardContainer) {
      const rect = dashboardContainer.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(dashboardContainer);
      console.log('DASHBOARD CONTAINER:');
      console.log('  - width:', rect.width);
      console.log('  - computed width:', computedStyle.width);
      console.log('  - computed max-width:', computedStyle.maxWidth);
      console.log('  - offsetWidth:', dashboardContainer.offsetWidth);
      console.log('  - scrollWidth:', dashboardContainer.scrollWidth);
    }
    
    // DEBUG: Verificar elementos stats-container
    const statsContainers = document.querySelectorAll('.stats-container, .stats-grid, .admin-stats-container, .user-profile-card');
    statsContainers.forEach((container, index) => {
      if (container) {
        const rect = container.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(container);
        console.log(`Container ${index} (${container.className}):`);
        console.log('  - width:', rect.width);
        console.log('  - computed width:', computedStyle.width);
        console.log('  - computed max-width:', computedStyle.maxWidth);
        console.log('  - offsetWidth:', container.offsetWidth);
        console.log('  - scrollWidth:', container.scrollWidth);
      }
    });
    console.log('======================');
    
    // Mostrar detalles de cada punto de venta para depuración
    if (pointsOfSale && pointsOfSale.length > 0) {
      console.log('DEBUG FRONTEND - Detalles de todos los puntos de venta:');
      pointsOfSale.forEach((pos, index) => {
        console.log(`DEBUG FRONTEND - Punto de venta ${index + 1}:`, {
          id: pos.id,
          name: pos.name,
          pos_closed: pos.pos_closed,
          business_id: pos.business_id
        });
      });
    }
    
    // Solo actualizar el estado del menú si no estamos cargando datos
    if (!menuLoading) {
      // Verificar si existe un registro de suscripción
      if (!hasSubscription && user && user.role === 'admin') {
        console.log('Setting menuEnabled to false - no subscription for admin');
        setMenuEnabled(false);
      } else if (pointsOfSale.length === 0) {
        console.log('Setting menuEnabled to false - no points of sale');
        setMenuEnabled(false);
      } else if (allPointsOfSaleClosed) {
        console.log('Setting menuEnabled to false - all points of sale closed');
        setMenuEnabled(false);
      } else if (subscriptionSuspended) {
        console.log('Setting menuEnabled to false - subscription suspended');
        setMenuEnabled(false);
      } else if (hasActivePointsOfSale && hasSubscription) {
        console.log('Setting menuEnabled to true - has active POS and subscription');
        setMenuEnabled(true);
      } else {
        console.log('Setting menuEnabled to false - fallback condition');
        setMenuEnabled(false);
      }
    }
  }, [hasActivePointsOfSale, allPointsOfSaleClosed, subscriptionSuspended, menuLoading, pointsOfSale, subscription, hasSubscription, user]);

  // Handle business form submission
  const handleBusinessAdded = (newBusiness, openPOSForm = false) => {
    setBusiness(newBusiness);
    setShowBusinessForm(false);
    
    // Si se indica abrir el formulario de punto de venta, lo abrimos automáticamente
    if (openPOSForm) {
      setShowPointOfSaleForm(true);
    }
  };
  
  // Handle point of sale form submission
  const handlePointOfSaleAdded = (newPointOfSale) => {
    setPointsOfSale([...pointsOfSale, newPointOfSale]);
    setShowPointOfSaleForm(false);
    fetchAdminRoleStats(); // Refresh stats after adding a new point of sale
    
    // Si es el primer punto de venta (matriz), hacer refresh de la página
    // para actualizar los datos y habilitar las opciones según el plan
    if (pointsOfSale.length === 0) {
      // Pequeño retraso para asegurar que los datos se guarden correctamente
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  // Handle closing a point of sale
  const handleClosePointOfSale = async (id) => {
    try {
      setPointsOfSaleLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`/api/points-of-sale/${id}/close`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('DEBUG - Punto de venta cerrado con ID:', id);
      
      // Refresh points of sale list and stats
      await fetchPointsOfSale();
      
      // Actualizar la información de suscripción para reflejar el cambio en la cuota mensual
      await fetchSubscription();
      
      // Verificar si todavía hay puntos de venta activos después del cierre
      const response = await axios.get('/api/points-of-sale', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.metadata) {
        const hasActive = response.data.metadata.hasActivePointsOfSale === true;
        const hasPointsOfSale = response.data.metadata.total > 0;
        const allClosed = hasPointsOfSale && !hasActive;
        
        console.log('DEBUG - Después de cerrar POS - ¿Hay puntos de venta activos?', hasActive);
        console.log('DEBUG - Después de cerrar POS - ¿Todos los puntos de venta están cerrados?', allClosed);
        
        // Actualizar el estado para reflejar la nueva situación
        setHasActivePointsOfSale(hasActive);
        // Ya no actualizamos directamente el estado del menú aquí
        // El useEffect dedicado se encargará de actualizar el estado del menú
        // basado en los cambios en hasActivePointsOfSale y subscriptionSuspended
        console.log("DEBUG - Después de cerrar POS - Actualizando hasActivePointsOfSale a", hasActive);
        console.log("DEBUG - Después de cerrar POS - Valores actuales:", {
          hasActive,
          subscriptionSuspended,
          'hasActive && !subscriptionSuspended': hasActive && !subscriptionSuspended
        });
        setAllPointsOfSaleClosed(allClosed);
      }
      
      fetchAdminRoleStats();
    } catch (error) {
      console.error('Error closing point of sale:', error);
      
      // Manejar los diferentes tipos de errores que pueden ocurrir
      let errorMessage = 'Error al cerrar el punto de venta';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        
        // Mostrar información adicional si está disponible
        if (error.response.data.currentStatus) {
          errorMessage += `\nEstado actual de la suscripción: ${error.response.data.currentStatus}`;
        }
      }
      
      // Mostrar el modal en lugar de la alerta
      setModalTitle('No se puede cerrar el punto de venta');
      setModalMessage(errorMessage);
      setModalType('error');
      setModalOpen(true);
    } finally {
      setPointsOfSaleLoading(false);
    }
  };

  // Handle reopening a point of sale
  const handleReopenPointOfSale = async (id) => {
    try {
      setPointsOfSaleLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`/api/points-of-sale/${id}/reopen`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('DEBUG - Punto de venta reabierto con ID:', id);
      
      // Refresh points of sale list and stats
      await fetchPointsOfSale();
      
      // Actualizar la información de suscripción para reflejar el cambio en la cuota mensual
      await fetchSubscription();
      
      // Verificar el estado de los puntos de venta después de la reapertura
      const response = await axios.get('/api/points-of-sale', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.metadata) {
        const hasActive = response.data.metadata.hasActivePointsOfSale === true;
        const hasPointsOfSale = response.data.metadata.total > 0;
        const allClosed = hasPointsOfSale && !hasActive;
        
        console.log('DEBUG - Después de reabrir POS - ¿Hay puntos de venta activos?', hasActive);
        // Ya no actualizamos directamente el estado del menú aquí
        // El useEffect dedicado se encargará de actualizar el estado del menú
        // basado en los cambios en hasActivePointsOfSale y subscriptionSuspended
        console.log("DEBUG - Después de reabrir POS - Actualizando hasActivePointsOfSale a", hasActive);
        console.log("DEBUG - Después de reabrir POS - Valores actuales:", {
          hasActive,
          subscriptionSuspended,
          'hasActive && !subscriptionSuspended': hasActive && !subscriptionSuspended
        });
        console.log('DEBUG - Después de reabrir POS - ¿Todos los puntos de venta están cerrados?', allClosed);
        
        // Actualizar el estado para reflejar la nueva situación
        setHasActivePointsOfSale(hasActive);
        setAllPointsOfSaleClosed(allClosed);
      }
      
      fetchAdminRoleStats();
    } catch (error) {
      console.error('Error reopening point of sale:', error);
      
      // Mostrar el modal en lugar de la alerta
      setModalTitle('Error al reabrir el punto de venta');
      setModalMessage(error.response?.data?.error || 'No se pudo reabrir el punto de venta. Por favor, inténtelo de nuevo.');
      setModalType('error');
      setModalOpen(true);
    } finally {
      setPointsOfSaleLoading(false);
    }
  };

  // Calculate days remaining until subscription expiration
  const calculateDaysRemaining = (expirationDate) => {
    if (!expirationDate) return 0;
    
    const today = new Date();
    const expDate = new Date(expirationDate);
    const diffTime = expDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };
  
  // Determinar si se deben mostrar las tarjetas de planes de suscripción
  const shouldShowSubscriptionPlans = (subscription) => {
    if (!subscription) return false;
    
    // Verificar si el estado de suscripción es 'trial'/'prueba' o 'vencido'
    const isTrialOrExpired = (
      subscription.status?.toLowerCase() === 'trial' ||
      subscription.status?.toLowerCase() === 'prueba' ||
      subscription.status?.toLowerCase() === 'vencido'
    );
    
    // Verificar si quedan 5 días o menos para que finalice el período
    const daysRemaining = calculateDaysRemaining(subscription.expiration_date);
    const isNearingEnd = daysRemaining <= 5;
    
    // Caso 1: Usuario con suscripción trial próxima a vencer
    const isTrialType = subscription.type_subscript?.toLowerCase() === 'trial';
    const showForTrialEnding = isTrialOrExpired && isTrialType && isNearingEnd;
    
    // Caso 2: Usuario admin con suscripción personal próxima a vencer
    const isPersonalType = subscription.type_subscript?.toLowerCase() === 'personal';
    const showForPersonalEnding = user?.role === 'admin' && isPersonalType && isNearingEnd;
    
    // Mostrar las tarjetas si se cumple alguna de las condiciones
    return showForTrialEnding || showForPersonalEnding;
  };
  
  // Determinar si se deben mostrar las tarjetas de información de pago
  const shouldShowPaymentCards = (subscription) => {
    if (!subscription) return false;
    
    // Verificar si el tipo de suscripción es 'negocio'
    const isBusinessType = subscription.type_subscript?.toLowerCase() === 'negocio';
    
    // Calcular días restantes hasta la fecha de vencimiento
    const daysRemaining = calculateDaysRemaining(subscription.expiration_date);
    
    // Mostrar las tarjetas si es tipo negocio y está dentro del rango de tiempo
    // (5 días antes hasta 7 días después de la fecha de vencimiento)
    return isBusinessType && daysRemaining <= 5 && daysRemaining >= -7;
  };
  
  // Manejar la selección de un plan de suscripción
  const handlePlanSelected = (plan) => {
    setSelectedPlan(plan);
    console.log(`Plan seleccionado: ${plan}`);
    // Aquí se podría implementar la lógica para guardar la selección del usuario
    // o mostrar un modal de confirmación, etc.
  };

  // Format subscription status for display
  const formatSubscriptionStatus = (status) => {
    if (!status) return 'No disponible';
    
    const statusMap = {
      'vigente': 'Vigente',
      'trial': 'Prueba',
      'prueba': 'Prueba', // Añadido para manejar 'Prueba' como equivalente a 'trial'
      'vencido': 'Vencido',
      'pending': 'Pendiente',
      'suspendido': 'Suspendido'
    };
    
    return statusMap[status.toLowerCase()] || status;
  };
  
  // Format subscription type for display
  const formatSubscriptionType = (type) => {
    if (!type) return 'No especificado';
    
    // Si el tipo es 'trial', mostrar como 'Plan Negocio Gratuito (30 días)'
    if (type.toLowerCase() === 'trial') {
      return 'Plan Negocio Gratuito (30 días)';
    }
    
    // Para otros tipos, mostrar el valor original
    return type;
  };

  // Format expiration date to ensure consistent display
  const formatExpirationDate = (dateString) => {
    if (!dateString) return 'No disponible';
    
    try {
      // Intentar extraer la fecha independientemente del formato
      // Primero verificar si es una fecha ISO completa (con tiempo y zona horaria)
      if (dateString.includes('T')) {
        // Crear un objeto Date y formatear solo la parte de la fecha
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
        }
      }
      
      // Si es un formato YYYY-MM-DD simple
      if (dateString.includes('-') && dateString.split('-').length === 3) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }
      
      // Si tiene otro formato que incluye '/' (posiblemente ya procesado incorrectamente)
      if (dateString.includes('/')) {
        // Intentar extraer solo las partes de la fecha
        const dateMatch = dateString.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (dateMatch && dateMatch.length === 4) {
          return `${dateMatch[1]}/${dateMatch[2]}/${dateMatch[3]}`;
        }
      }
    } catch (error) {
      console.error('Error al formatear la fecha:', error);
    }
    
    // Si ninguno de los formatos anteriores funciona, devolver un formato legible
    // o la fecha original si no podemos procesarla
    return dateString;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRegistrarGastoClick = (e) => {
    // Prevenir comportamiento por defecto para asegurar que el evento se maneje correctamente
    e.preventDefault();
    // Redireccionar a la página de registro de gastos
    console.log('Redirigiendo a la página de registro de gastos...');
    navigate('/register-expense');
  };

  const handleReportesClick = () => {
    // Redireccionar a la página de Reportes usando el hook navigate
    navigate('/reports');
  };


  const handleUsuariosClick = () => {
    // Verificar si la suscripción es de tipo 'personal' y si ya tiene vendedores asignados
    if (subscription && subscription.type_subscript?.toLowerCase() === 'personal' && hasAssignedSellers) {
      // Si es 'personal' y ya tiene vendedores asignados, mostrar un mensaje en lugar de navegar
      setModalOpen(true);
      setModalTitle('Acceso Restringido');
      setModalMessage('No puedes agregar más vendedores con el plan personal. Actualiza tu suscripción para agregar más vendedores.');
      setModalType('error');
    } else {
      // Para otros tipos de suscripción o si aún no tiene vendedores asignados, permitir la navegación
      navigate('/sellers');
    }
  };

  const handleProductosClick = () => {
    // Verificar si la suscripción es de tipo 'personal'
    if (subscription && subscription.type_subscript?.toLowerCase() === 'personal') {
      // Si es 'personal', mostrar un mensaje en lugar de navegar
      setModalOpen(true);
      setModalTitle('Acceso Restringido');
      setModalMessage('La gestión de productos no está disponible en el plan personal. Actualice su suscripción para acceder a esta funcionalidad.');
      setModalType('error');
    } else {
      // Para otros tipos de suscripción, permitir la navegación
      navigate('/products');
    }
  };

  const handleClientesClick = () => {
    // Verificar si la suscripción es de tipo 'personal'
    if (subscription && subscription.type_subscript?.toLowerCase() === 'personal') {
      // Si es 'personal', mostrar un mensaje en lugar de navegar
      setModalOpen(true);
      setModalTitle('Acceso Restringido');
      setModalMessage('La gestión de clientes no está disponible en el plan personal. Actualice su suscripción para acceder a esta funcionalidad.');
      setModalType('error');
    } else {
      // Para otros tipos de suscripción, permitir la navegación
      navigate('/customers');
    }
  };

  const handleGastosClick = () => {
    // Redireccionar a la página de Tipos de Gastos usando el hook navigate
    navigate('/expense-types');
  };

  const handleRegistrarVentaClick = () => {
    // Verificar si la suscripción es de tipo 'personal'
    if (subscription && subscription.type_subscript?.toLowerCase() === 'personal') {
      // Si es 'personal', pasar un parámetro de estado indicando que la venta detallada está deshabilitada
      navigate('/sales', { state: { disableDetailedSale: true } });
    } else {
      // Para otros tipos de suscripción, permitir ambos tipos de venta
      navigate('/sales');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Funciones para manejar la navegación a las diferentes secciones
  

  return (
    <>
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>Ventaz Web Dashboard</h1>
        <div className="user-info">
          {user && (
            <>
              <span>Bienvenido, {user.full_name}</span>
              <button onClick={handleLogout} className="logout-btn">
                Cerrar Sesión
              </button>
            </>
          )}
        </div>
      </header>

      {/* Mensaje informativo cuando no hay puntos de venta registrados */}
      {console.log('DEBUG - Estado actual para mensaje de no hay puntos de venta:', {
        hasActivePointsOfSale,
        allPointsOfSaleClosed,
        menuEnabled,
        'pointsOfSale.length': pointsOfSale.length
      })}
      {user && user.role === 'admin' && pointsOfSale.length === 0 && (
        <div className="info-message">
          <p>
            <strong>Importante:</strong> Para acceder a todas las funcionalidades, primero debe registrar tu negocio y el punto de venta matriz o principal.
          </p>
        </div>
      )}

      {/* Mensaje informativo cuando la suscripción está suspendida */}
      {user && user.role === 'admin' && subscriptionSuspended && (
        <div className="info-message warning">
          <p>
            <strong>Importante:</strong> Su suscripción está suspendida. Las funcionalidades de registro de ventas y gastos están deshabilitadas. Por favor, contacte con soporte para reactivar su suscripción.
          </p>
        </div>
      )}
      
      {/* Mensaje informativo cuando no hay puntos de venta abiertos */}
      {user && user.role === 'admin' && allPointsOfSaleClosed && (
        <div className="info-message warning">
          <p>
            <strong>Importante:</strong> No tiene puntos de venta abiertos. Para acceder a todas las funcionalidades, debe abrir al menos un punto de venta.
          </p>
        </div>
      )}

      {/* Botón destacado para usuarios admin */}
      {console.log('=== RENDER DIMENSION DEBUG ===') || 
       console.log('window.innerWidth:', window.innerWidth) ||
       console.log('100vw calculated:', window.innerWidth + 'px') ||
       (() => {
         const statsContainers = document.querySelectorAll('.stats-container, .stats-grid, .admin-stats-container');
         statsContainers.forEach((container, index) => {
           if (container) {
             const rect = container.getBoundingClientRect();
             const computedStyle = window.getComputedStyle(container);
             console.log(`RENDER Container ${index} (${container.className}):`);
             console.log('  - width:', rect.width);
             console.log('  - computed width:', computedStyle.width);
             console.log('  - computed max-width:', computedStyle.maxWidth);
           }
         });
         console.log('===============================');
       })() ||
       console.log('DEBUG FRONTEND - Estado del menú al renderizar botones:', {
        menuEnabled,
        menuLoading,
        hasActivePointsOfSale,
        allPointsOfSaleClosed,
        subscriptionSuspended,
        'pointsOfSale.length': pointsOfSale.length,
        'subscription': subscription ? 'Existe' : 'No existe',
        'userId': user?.id
      })}
      {user && user.role === 'admin' && (
        <div className="highlighted-actions">
          <button onClick={handleRegistrarVentaClick} className={`action-button sales-button ${(!menuEnabled || !hasSubscription) ? 'disabled' : ''}`} disabled={!menuEnabled || !hasSubscription}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            Registrar Ventas
          </button>
          <button onClick={handleRegistrarGastoClick} className={`action-button expenses-button ${(!menuEnabled || !hasSubscription) ? 'disabled' : ''}`} disabled={!menuEnabled || !hasSubscription}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"></rect>
              <line x1="2" y1="10" x2="22" y2="10"></line>
              <line x1="6" y1="14" x2="6" y2="18"></line>
              <line x1="12" y1="14" x2="12" y2="18"></line>
              <line x1="18" y1="14" x2="18" y2="18"></line>
            </svg>
            Registrar Gastos
          </button>
        </div>
      )}

      {/* Menú de navegación para usuarios admin */}
      {user && user.role === 'admin' && (
        <nav className="admin-menu">
          <ul className="admin-menu-list">
            <li className="admin-menu-item">
              <button onClick={handleUsuariosClick} className={`admin-menu-link ${(!menuEnabled || !hasSubscription || (subscription && subscription.type_subscript?.toLowerCase() === 'personal' && hasAssignedSellers)) ? 'disabled' : ''}`} disabled={!menuEnabled || !hasSubscription || (subscription && subscription.type_subscript?.toLowerCase() === 'personal' && hasAssignedSellers)} title={(subscription && subscription.type_subscript?.toLowerCase() === 'personal' && hasAssignedSellers) ? 'No puedes agregar más usuarios con el plan personal' : 'Gestionar usuarios'}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                Usuarios
              </button>
            </li>
            <li className="admin-menu-item">
              <button onClick={handleProductosClick} className={`admin-menu-link ${(!menuEnabled || !hasSubscription || (subscription && subscription.type_subscript?.toLowerCase() === 'personal')) ? 'disabled' : ''}`} disabled={!menuEnabled || !hasSubscription || (subscription && subscription.type_subscript?.toLowerCase() === 'personal')} title={(subscription && subscription.type_subscript?.toLowerCase() === 'personal') ? 'No disponible en suscripción personal' : 'Gestionar productos'}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
                Productos
              </button>
            </li>
            <li className="admin-menu-item">
              <button onClick={handleClientesClick} className={`admin-menu-link ${(!menuEnabled || !hasSubscription || (subscription && subscription.type_subscript?.toLowerCase() === 'personal')) ? 'disabled' : ''}`} disabled={!menuEnabled || !hasSubscription || (subscription && subscription.type_subscript?.toLowerCase() === 'personal')} title={(subscription && subscription.type_subscript?.toLowerCase() === 'personal') ? 'No disponible en suscripción personal' : 'Gestionar clientes'}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Clientes
              </button>
            </li>
            <li className="admin-menu-item">
              <button onClick={handleGastosClick} className={`admin-menu-link ${(!menuEnabled || !hasSubscription) ? 'disabled' : ''}`} disabled={!menuEnabled || !hasSubscription}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Gastos
              </button>
            </li>
            <li className="admin-menu-item">
              <button onClick={handleReportesClick} className={`admin-menu-link ${(!menuEnabled || !hasSubscription) ? 'disabled' : ''}`} disabled={!menuEnabled || !hasSubscription}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                  <line x1="2" y1="10" x2="22" y2="10"></line>
                  <line x1="6" y1="14" x2="6" y2="18"></line>
                  <line x1="12" y1="14" x2="12" y2="18"></line>
                  <line x1="18" y1="14" x2="18" y2="18"></line>
                </svg>
                Reportes
              </button>
            </li>
          </ul>
          
        </nav>
      )}

      <div className="dashboard-content">
        <div className="user-profile-card">
          <h2>Perfil del Usuario</h2>
          {user && (
            <div className="profile-details">
              <p><strong>Nombre:</strong> {user.full_name}</p>
              <p><strong>Email:</strong> {user.email}</p>
              <p><strong>Rol:</strong> {user.role}</p>
              
              {user.role === 'admin' && (
                <div className="subscription-details">
                  <h3>Información de Suscripción</h3>
                  {subscriptionLoading ? (
                    <p>Cargando información de suscripción...</p>
                  ) : subscription ? (
                    <>
                      <p><strong>Estado de Suscripción:</strong> <span className={`subscription-status ${subscription.status?.toLowerCase()}`}>{formatSubscriptionStatus(subscription.status)}</span></p>
                      <p><strong>Tipo de Suscripción:</strong> <span className="subscription-type">{formatSubscriptionType(subscription.type_subscript)}</span></p>
                      <p><strong>Días Restantes:</strong> <span className="days-remaining">{calculateDaysRemaining(subscription.expiration_date)} días</span></p>
                      <p><strong>Cuota Mensual:</strong> 
                        {currencyRate ? (
                          <span className="monthly-fee">
                            {currencyRate.currency_code} {(subscription.monthly_fee * parseFloat(currencyRate.exchange_rate)).toFixed(2)}
                            {parseFloat(currencyRate.exchange_rate) !== 1.0000 && (
                              <span style={{color: '#666', fontSize: '0.9em', marginLeft: '8px'}}>
                                (${subscription.monthly_fee?.toFixed(2)} USD)
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="monthly-fee">
                            ${subscription.monthly_fee?.toFixed(2)}
                          </span>
                        )}
                      </p>
                      <p><strong>Fecha de Vencimiento:</strong> <span className="expiration-date">{formatExpirationDate(subscription.expiration_date)}</span></p>
                      
                      {/* Mostrar tarjetas de planes de suscripción si se cumplen las condiciones */}
                      {shouldShowSubscriptionPlans(subscription) && (
                        <SubscriptionPlans onPlanSelected={handlePlanSelected} />
                      )}
                      
                      {/* Mostrar tarjetas de información de pago si se cumplen las condiciones */}
                      {shouldShowPaymentCards(subscription) && (
                        <PaymentInfoCards />
                      )}
                    </>
                  ) : (
                    <p>No se encontró información de suscripción.</p>
                  )}
                </div>
              )}
              
              {/* Estadísticas de Ventas */}
              {user.role === 'admin' && (
                <div className="sales-stats-details">
                  <h3>Estadísticas de Ventas</h3>
                  
                  {/* Selector de Punto de Venta */}
                  {salesStats && salesStats.pointsOfSale && (
                    <div className="pos-selector">
                      <label htmlFor="pos-filter">Filtrar por Punto de Venta: </label>
                      <select 
                        id="pos-filter" 
                        value={selectedPOS} 
                        onChange={handlePOSChange}
                        className="pos-filter-select"
                      >
                        <option value="all">Todos los Puntos de Venta</option>
                        {salesStats.pointsOfSale.map(pos => (
                          <option key={pos.id} value={pos.id}>{pos.pos_name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {salesStatsLoading ? (
                    <p>Cargando estadísticas de ventas...</p>
                  ) : salesStats ? (
                    <div className="stats-grid">
                      <div className="stat-card">
                        <h3>Ventas del Día</h3>
                        <p className="stat-value">${salesStats.dailyTotal.toFixed(2)}</p>
                        <p className="stat-count">{salesStats.dailySalesCount} ventas</p>
                      </div>
                      <div className="stat-card">
                        <h3>Ventas Semana Actual</h3>
                        <p className="stat-value">${salesStats.weeklyTotal.toFixed(2)}</p>
                        <p className="stat-count">{salesStats.weeklySalesCount} ventas</p>
                      </div>
                      <div className="stat-card">
                        <h3>Ventas del Mes</h3>
                        <p className="stat-value">${salesStats.monthlyTotal.toFixed(2)}</p>
                        <p className="stat-count">{salesStats.monthlySalesCount} ventas</p>
                      </div>
                    </div>
                  ) : (
                    <div className="stats-grid">
                      <div className="stat-card">
                        <h3>Ventas del Día</h3>
                        <p className="stat-value">$0.00</p>
                        <p className="stat-count">0 ventas</p>
                      </div>
                      <div className="stat-card">
                        <h3>Ventas Semana Actual</h3>
                        <p className="stat-value">$0.00</p>
                        <p className="stat-count">0 ventas</p>
                      </div>
                      <div className="stat-card">
                        <h3>Ventas del Mes</h3>
                        <p className="stat-value">$0.00</p>
                        <p className="stat-count">0 ventas</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Gráficas para el dashboard del admin */}
                  <AdminDashboardCharts selectedPOS={selectedPOS} />

                  {/* Las tablas de ventas recientes ya se muestran a través del componente AdminDashboardCharts */}


                  {/* Productos TOP - Oculto permanentemente por solicitud del usuario */}
                  {/* {user && user.role === 'admin' && business && (
                    <AdminTopProducts selectedPOS={selectedPOS} />
                  )} */}

                  {/* La gráfica de Gastos Mensuales por Categoría se ha movido al componente SalesCharts */}
                </div>
              )}
            </div>

          )}
        </div>

        {/* Master User Stats Section */}
        {user && user.role === 'master' && (
          <div className="master-stats-section">
            <h2>User Management Statistics</h2>
            {statsLoading ? (
              <div className="loading">Loading statistics...</div>
            ) : statsError ? (
              <div className="error-message">{statsError}</div>
            ) : adminStats ? (
              <div className="admin-stats-container">
                <div className="stat-card admin-stat">
                  <h3>Verified Admin Users</h3>
                  <p className="stat-value">{adminStats.adminVerified}</p>
                </div>
                <div className="stat-card admin-stat">
                  <h3>Unverified Admin Users</h3>
                  <p className="stat-value">{adminStats.adminUnverified}</p>
                </div>
                <div className="stat-card admin-stat">
                  <h3>Total Users</h3>
                  <p className="stat-value">{adminStats.total}</p>
                </div>
                <div className="stat-card admin-stat">
                  <h3>Current Month Users</h3>
                  <p className="stat-value">{adminStats.currentMonthTotal}</p>
                </div>
                <div className="stat-card admin-stat">
                  <h3>Current Month Verified</h3>
                  <p className="stat-value">{adminStats.currentMonthVerified}</p>
                </div>
                <div className="stat-card payment-stat">
                  <h3>Year Payments Total</h3>
                  <p className="stat-value">${adminStats.yearTotal?.toLocaleString() || '0.00'}</p>
                </div>
                <div className="stat-card payment-stat">
                  <h3>Month Payments Total</h3>
                  <p className="stat-value">${adminStats.monthTotal?.toLocaleString() || '0.00'}</p>
                </div>
                <div className="stat-card payment-stat">
                  <h3>Week Payments Total</h3>
                  <p className="stat-value">${adminStats.weekTotal?.toLocaleString() || '0.00'}</p>
                </div>
              </div>
            ) : (
              <p>No statistics available</p>
            )}

            {adminStats && adminStats.recentUsers && adminStats.recentUsers.length > 0 && (
              <div className="recent-users-section">
                <h3>Recently Registered Admin Users</h3>
                <table className="recent-users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Registered On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminStats.recentUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.full_name}</td>
                        <td>{user.email}</td>
                        <td>{user.role}</td>
                        <td>{user.is_verified ? 'Verified' : 'Unverified'}</td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Admin Subscriptions Table */}
            <AdminSubscriptionsTable />
          </div>
        )}

        {/* Admin User Stats Section */}
        {user && user.role === 'admin' && (
          <div className="admin-role-stats-section">
            {/* Business Management Section */}
            <div className="business-management-section">
              <h2>Gestión de Negocio</h2>
              
              {businessLoading ? (
                <div className="loading">Cargando información del negocio...</div>
              ) : showBusinessForm ? (
                <BusinessForm 
                  onBusinessAdded={handleBusinessAdded} 
                  onCancel={() => setShowBusinessForm(false)} 
                />
              ) : business ? (
                <div className="business-card">
                  <h3>{business.bis_name}</h3>
                  <div className="business-detail">
                    <strong>Teléfono:</strong> <span>{business.bis_phone}</span>
                  </div>
                  <div className="business-detail">
                    <strong>País:</strong> <span>{business.bis_country}</span>
                  </div>
                  
                  {/* Points of Sale Section */}
                  <div className="points-of-sale-section">
                    <h4>Puntos de Venta</h4>
                    
                    {pointsOfSaleLoading ? (
                      <div className="loading">Cargando puntos de venta...</div>
                    ) : showPointOfSaleForm ? (
                      <PointOfSaleForm 
                        onPointOfSaleAdded={handlePointOfSaleAdded}
                        onCancel={() => setShowPointOfSaleForm(false)}
                        isFirstPointOfSale={false} // Indicar que no es el primer punto de venta
                      />
                    ) : (
                      <div>
                        {pointsOfSale.length > 0 ? (
                          <div className="points-of-sale-list">
                            {pointsOfSale.map(pos => (
                              <div key={pos.id} className="pos-item">
                                <h5>{pos.pos_name}</h5>
                                {pos.pos_address && (
                                  <div className="pos-detail">
                                    <strong>Dirección:</strong> <span>{pos.pos_address}</span>
                                  </div>
                                )}
                                {pos.pos_phone && (
                                  <div className="pos-detail">
                                    <strong>Teléfono:</strong> <span>{pos.pos_phone}</span>
                                  </div>
                                )}
                                {pos.pos_city && (
                                  <div className="pos-detail">
                                    <strong>Ciudad:</strong> <span>{pos.pos_city}</span>
                                  </div>
                                )}
                                {pos.pos_region && (
                                  <div className="pos-detail">
                                    <strong>Región:</strong> <span>{pos.pos_region}</span>
                                  </div>
                                )}
                                <div className="pos-status">
                                  <strong>Estado:</strong> <span>{pos.pos_closed ? 'Cerrado' : 'Abierto'}</span>
                                </div>
                                <div className="pos-actions">
                                  {pos.pos_closed ? (
                                    <button 
                                      className="reopen-pos-btn" 
                                      onClick={() => handleReopenPointOfSale(pos.id)}
                                      disabled={pointsOfSaleLoading}
                                    >
                                      {pointsOfSaleLoading ? 'Procesando...' : 'Reabrir'}
                                    </button>
                                  ) : (
                                    <button 
                                      className="close-pos-btn" 
                                      onClick={() => handleClosePointOfSale(pos.id)}
                                      disabled={pointsOfSaleLoading}
                                    >
                                      {pointsOfSaleLoading ? 'Procesando...' : 'Cerrar'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p>No tienes puntos de venta registrados.</p>
                        )}
                        
                        <button 
                          className="add-pos-btn" 
                          onClick={() => setShowPointOfSaleForm(true)}
                          disabled={subscription && (subscription.type_subscript === 'trial' || subscription.type_subscript === 'personal') && pointsOfSale.length > 0}
                          title={subscription && (subscription.type_subscript === 'trial' || subscription.type_subscript === 'personal') && pointsOfSale.length > 0 ? 'No disponible en suscripción trial o personal con puntos de venta existentes' : 'Agregar un nuevo punto de venta'}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          Agregar Punto de Venta
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <p>No tienes un negocio registrado.</p>
                  <button 
                    className="add-business-btn" 
                    onClick={() => setShowBusinessForm(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Agregar tu negocio
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Regular User Stats Section */}
        {user && user.role !== 'master' && user.role !== 'admin' && userStats && (
          <div className="stats-container">
            <div className="stat-card">
              <h3>Total Sales</h3>
              <p className="stat-value">{userStats.totalSales}</p>
            </div>
            <div className="stat-card">
              <h3>Pending Orders</h3>
              <p className="stat-value">{userStats.pendingOrders}</p>
            </div>
          </div>
        )}


        {/* Regular User Activity Section */}
        {user && user.role !== 'master' && user.role !== 'admin' && (
          <div className="recent-activity">
            <h2>Recent Activity</h2>
            {userStats && userStats.recentActivity && userStats.recentActivity.length > 0 ? (
              <ul>
                {userStats.recentActivity.map((activity, index) => (
                  <li key={index}>{activity}</li>
                ))}
              </ul>
            ) : (
              <p>No recent activity</p>
            )}
          </div>
        )}
        
        {/* Las secciones de Ventas Recientes y Productos Top ya están incluidas en el dashboard */}
      </div>
      
      {/* Modal para mensajes de error y notificaciones */}
      <Modal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        title={modalTitle} 
        message={modalMessage} 
        type={modalType} 
      />
    </div>
    
    {/* Asistente IA Ventasz - Fuera del contenedor para position fixed */}
    <ChatWidget />
    </>
  );
};

export default Dashboard;
